import { type ReactNode, useId } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ProductionPlaceFormValues } from '@/features/production-declaration/schema/production-place-schema';

type ForestTypeOption = {
  label: string;
  value: string;
};

type ProductionPlaceDetailsFieldsProps = {
  control: Control<ProductionPlaceFormValues>;
  countryOptions: ForestTypeOption[];
  countryRiskBadge?: ReactNode;
  isCountriesLoading: boolean;
  disabled: boolean;
  fieldClassName: string;
  forestTypeOptions: ForestTypeOption[];
  isForestTypesLoading: boolean;
  labelClassName: string;
};

export function ProductionPlaceDetailsFields({
  control,
  countryOptions,
  countryRiskBadge,
  isCountriesLoading,
  disabled,
  fieldClassName,
  forestTypeOptions,
  isForestTypesLoading,
  labelClassName,
}: ProductionPlaceDetailsFieldsProps) {
  const { t } = useTranslation('productionDeclaration');
  const descriptionId = useId();
  const forestTypeId = useId();
  const timeRangeId = useId();
  const countryId = useId();

  return (
    <>
      <div className='grid gap-4 md:grid-cols-2'>
        <div className='space-y-2'>
          <Label className={labelClassName} htmlFor={descriptionId}>
            {t(
              'materialCommodityOrigins.cards.productionPlace.fields.description',
            )}{' '}
            *
          </Label>
          <Controller
            control={control}
            name='description'
            render={({ field, fieldState }) => (
              <>
                <Input
                  aria-required='true'
                  className={fieldClassName}
                  id={descriptionId}
                  placeholder={t(
                    'materialCommodityOrigins.cards.productionPlace.fields.descriptionPlaceholder',
                  )}
                  {...field}
                />
                <FormErrorMessage
                  error={fieldState.error?.message}
                  ns='productionDeclaration'
                />
              </>
            )}
          />
        </div>

        <div className='space-y-2'>
          <Label className={labelClassName} htmlFor={forestTypeId}>
            {t(
              'materialCommodityOrigins.cards.productionPlace.fields.forestType',
            )}{' '}
            *
          </Label>
          <Controller
            control={control}
            name='forestTypeId'
            render={({ field, fieldState }) => (
              <>
                <Select
                  disabled={isForestTypesLoading || disabled}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger
                    aria-required='true'
                    className='h-12 min-h-12 w-full rounded-lg border-declaration-border bg-white px-4 py-0 text-base text-declaration-foreground shadow-none focus-visible:border-declaration-primary focus-visible:ring-declaration-primary/15 [&_[data-slot=select-value]]:text-base'
                    id={forestTypeId}
                  >
                    <SelectValue
                      placeholder={t(
                        'materialCommodityOrigins.cards.productionPlace.fields.forestTypePlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {forestTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormErrorMessage
                  error={fieldState.error?.message}
                  ns='productionDeclaration'
                />
              </>
            )}
          />
        </div>
      </div>

      <div className='grid gap-4 md:grid-cols-2'>
        <div className='space-y-2'>
          <div className='flex min-h-7 flex-wrap items-center gap-2'>
            <Label className={labelClassName} htmlFor={countryId}>
              {t(
                'materialCommodityOrigins.cards.productionPlace.fields.country',
              )}{' '}
              *
            </Label>
            {countryRiskBadge}
          </div>
          <Controller
            control={control}
            name='countryId'
            render={({ field, fieldState }) => (
              <>
                <Select
                  disabled={isCountriesLoading || disabled}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger
                    aria-invalid={fieldState.invalid}
                    aria-required='true'
                    className='h-12 min-h-12 w-full rounded-lg border-declaration-border bg-white px-4 py-0 text-base text-declaration-foreground shadow-none focus-visible:border-declaration-primary focus-visible:ring-declaration-primary/15 [&_[data-slot=select-value]]:text-base'
                    id={countryId}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  >
                    <SelectValue
                      placeholder={t(
                        'materialCommodityOrigins.cards.productionPlace.fields.countryPlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormErrorMessage
                  error={fieldState.error?.message}
                  ns='productionDeclaration'
                />
              </>
            )}
          />
        </div>
        <div className='space-y-2'>
          <Label className={`${labelClassName} min-h-7`} htmlFor={timeRangeId}>
            {t(
              'materialCommodityOrigins.cards.productionPlace.fields.timeRange',
            )}{' '}
            *
          </Label>
          <Controller
            control={control}
            name='productionTimeRange'
            render={({ field, fieldState }) => (
              <>
                <Input
                  aria-required='true'
                  className={fieldClassName}
                  id={timeRangeId}
                  placeholder={t(
                    'materialCommodityOrigins.cards.productionPlace.fields.timeRangePlaceholder',
                  )}
                  {...field}
                />
                <FormErrorMessage
                  error={fieldState.error?.message}
                  ns='productionDeclaration'
                />
              </>
            )}
          />
        </div>
      </div>
    </>
  );
}
